import { mergeAttributes, Node } from '@tiptap/core';
import TextAlign from '@tiptap/extension-text-align';
import StarterKit from '@tiptap/starter-kit';
import { TERM_VARIABLE_NAMES, termVariableLabel } from './variables';

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    variable: {
      insertVariable: (name: string) => ReturnType;
    };
  }
}

/**
 * Variável como nó inline atômico (um "chip" que se move/apaga inteiro). No HTML sai como
 * `<span data-variable="nome">{{nome}}</span>`; `fillTermVariables` troca pelo valor real.
 */
export const Variable = Node.create({
  name: 'variable',
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,

  addAttributes() {
    return {
      name: {
        default: null,
        parseHTML: (element) => element.getAttribute('data-variable'),
        renderHTML: (attributes) => ({ 'data-variable': attributes.name }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'span[data-variable]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    return [
      'span',
      mergeAttributes(HTMLAttributes, {
        class: 'term-variable',
        title: termVariableLabel(node.attrs.name),
      }),
      `{{${node.attrs.name}}}`,
    ];
  },

  renderText({ node }) {
    return `{{${node.attrs.name}}}`;
  },

  addCommands() {
    return {
      insertVariable:
        (name) =>
        ({ commands }) =>
          (TERM_VARIABLE_NAMES as string[]).includes(name)
            ? commands.insertContent({ type: this.name, attrs: { name } })
            : false,
    };
  },
});

/**
 * Extensões do documento — as MESMAS no editor (navegador) e no `generateHTML` do servidor.
 * Só o que faz sentido num termo impresso: sem link, código ou imagem.
 */
export const termExtensions = [
  StarterKit.configure({
    code: false,
    codeBlock: false,
    link: false,
    heading: { levels: [1, 2, 3] },
  }),
  TextAlign.configure({ types: ['heading', 'paragraph'] }),
  Variable,
];

/** Documento vazio do Tiptap. */
export const EMPTY_TERM_DOCUMENT = { type: 'doc', content: [{ type: 'paragraph' }] };
